import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLogo from './SiteLogo';
import { siteNavItems, navHref } from './siteNav';

interface SiteHeaderProps {
  current: string;
}

function SiteHeader({ current }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-300'
          : 'bg-background-50/60 backdrop-blur-sm border-b border-transparent'
      }`}
    >
      <div className="w-full h-[76px] px-4 md:px-8 lg:px-12 flex items-center justify-between">
        <SiteLogo />

        <nav className="hidden lg:flex items-center gap-8">
          {siteNavItems.map((item) => {
            const active = item.key === current;
            return (
              <Link
                key={item.key}
                to={navHref(item, current)}
                className={`relative flex items-center h-[76px] text-[14px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  active ? 'text-foreground-950' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {item.label}
                {active && (
                  <>
                    <span className="absolute top-[22px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                    <span className="absolute bottom-[22px] left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full bg-primary-500"></span>
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/how-it-works"
            className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[14px] font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
          >
            Explore EA
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-[15px]"></i>
            </span>
          </Link>

          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-300 bg-background-50 px-4 md:px-8 py-4 flex flex-col gap-1">
          {siteNavItems.map((item) => {
            const active = item.key === current;
            return (
              <Link
                key={item.key}
                to={navHref(item, current)}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2 py-2.5 text-[15px] font-medium transition-colors cursor-pointer ${
                  active ? 'text-primary-700' : 'text-foreground-800 hover:text-primary-600'
                }`}
              >
                {active && <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>}
                {item.label}
              </Link>
            );
          })}
          <Link
            to="/how-it-works"
            onClick={() => setMenuOpen(false)}
            className="mt-2 inline-flex items-center justify-center h-11 rounded-md bg-primary-500 text-background-50 text-[15px] font-semibold cursor-pointer"
          >
            Explore EA
          </Link>
        </div>
      )}
    </header>
  );
}

export default SiteHeader;